// ============================================================================================
// LES TYPES D'ÉQUIPEMENT, CÔTÉ ÉCRANS — la liste du référentiel « Types d'équipement », et la NATURE qui range un appareil.
//
// UTILITÉ : le formulaire d'ajout au parc propose les types du référentiel (plus une liste écrite en dur), et les écrans
// rangent un appareil par la nature de son type que le serveur renvoie (`DeviceRow.nature`) — jamais en cherchant un mot
// dans son libellé. La règle de ce que chaque nature DÉCLENCHE vit au serveur (`server/src/equipment-types.ts`).
// ============================================================================================
import type { IconName } from '@orgazone/ui-web';
import type { ReferentialRow } from './api';

/** La liste du référentiel qui porte les types. */
export const EQUIPMENT_TYPE_LIST = 'types_equipement';

/** La nature d'un type — miroir de la liste fermée du serveur. */
export type EquipmentNature = 'caisse' | 'mobile-partner' | 'tpe' | 'poste';

/** Ce que l'écran affiche d'une nature. */
export const NATURE_LABEL: Record<EquipmentNature, string> = {
  caisse: 'Caisse', 'mobile-partner': 'Mobile Partner', tpe: 'TPE', poste: 'Poste',
};

/**
 * Ce que chaque nature DÉCLENCHE à l'affectation, dit sur la tuile du type dans le formulaire du parc.
 * ⚠️ Le miroir des règles du serveur (`server/src/equipment-types.ts` : numéro de registre, préfixe d'identité
 *    magasin) : une phrase qui promettrait un code que le serveur n'attribue pas mentirait à l'entrée au parc.
 */
export const NATURE_HINT: Record<EquipmentNature, string> = {
  caisse: 'Encaissement — reçoit un numéro R… à l’affectation.',
  'mobile-partner': 'Terminal de stock au scan — reçoit un code MP… à l’affectation.',
  tpe: 'Terminal de paiement — reçoit un code TPE… à l’affectation.',
  poste: 'Poste du magasin — ni numéro ni code à l’affectation.',
};

/** La nature illisible d'un type : aucune règle n'en découle, et la tuile le dit. */
export const NO_NATURE_HINT = 'Nature non reconnue : rien ne se déclenche à l’affectation.';

/** L'icône de la tuile d'un type, par sa nature (les dessins de la maquette : ticket, scan, terminal, écran). */
export const NATURE_ICON: Record<EquipmentNature, IconName> = {
  caisse: 'receipt', 'mobile-partner': 'barcode', tpe: 'card', poste: 'monitor',
};

/** Un type proposé à l'ajout au parc. */
export interface EquipmentTypeOption { code: string; label: string; nature: EquipmentNature | null }

const NATURES = Object.keys(NATURE_LABEL) as EquipmentNature[];

/** Les types d'équipement d'une lecture des référentiels, dans l'ordre réglé au référentiel. */
export function equipmentTypesFrom(rows: readonly ReferentialRow[]): EquipmentTypeOption[] {
  return rows
    .filter((r) => r.list === EQUIPMENT_TYPE_LIST)
    .sort((a, b) => a.ord - b.ord || a.label.localeCompare(b.label))
    .map((r) => {
      const nature = r.attrs?.nature;
      return { code: r.code, label: r.label, nature: NATURES.includes(nature as EquipmentNature) ? (nature as EquipmentNature) : null };
    });
}
